import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '../../molecules/Modal';
import { FormField } from '../../molecules';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SelectField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const MultiSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 14px;
    color: #333;
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: #f8f9fa;
  color: #666;

  &:hover:not(:disabled) {
    background-color: #e9ecef;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #007bff;
  color: white;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const EditUserModal = ({ isOpen, onClose, user, onSave, teachers = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    studentName: '',
    birthYear: '',
    gender: 'male',
    isActive: true,
    teacher_ids: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        studentName: user.studentName || '',
        birthYear: user.birthYear || '',
        gender: user.gender || 'male',
        isActive: user.isActive !== undefined ? user.isActive : true,
        teacher_ids: user.teacher_ids || [],
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(user.id, formData);
      onClose();
    } catch (error) {
      console.error('수정 실패:', error);
      alert('수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const isMom = user.type === 'mom';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${isMom ? '학부모' : '치료사'} 정보 수정`}>
      <Form onSubmit={handleSubmit}>
        <FormField
          label={isMom ? "학부모 이름" : "이름"}
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />

        {!isMom && (
          <FormField
            label="이메일"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        )}

        <FormField
          label="전화번호"
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="010-1234-5678"
        />

        {isMom && (
          <>
            <FormField
              label="학생 이름"
              type="text"
              name="studentName"
              value={formData.studentName}
              onChange={handleChange}
              required
            />

            <FormField
              label="출생년도"
              type="number"
              name="birthYear"
              value={formData.birthYear}
              onChange={handleChange}
              required
              placeholder="2020"
              min="2000"
              max={new Date().getFullYear()}
            />

            <SelectField>
              <Label>성별</Label>
              <Select name="gender" value={formData.gender} onChange={handleChange}>
                <option value="male">남아</option>
                <option value="female">여아</option>
              </Select>
            </SelectField>

            <SelectField>
              <Label>배정된 튜터 (복수 선택 가능)</Label>
              <MultiSelectContainer>
                {teachers.length === 0 ? (
                  <span style={{ color: '#999', fontSize: '14px' }}>등록된 튜터가 없습니다</span>
                ) : (
                  teachers.map((teacher) => {
                    const teacherId = teacher.id || teacher.uid;
                    return (
                      <CheckboxItem key={teacherId}>
                        <Checkbox
                          type="checkbox"
                          id={`teacher-${teacherId}`}
                          checked={formData.teacher_ids.includes(teacherId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                teacher_ids: [...prev.teacher_ids, teacherId]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                teacher_ids: prev.teacher_ids.filter(id => id !== teacherId)
                              }));
                            }
                          }}
                        />
                        <label htmlFor={`teacher-${teacherId}`}>{teacher.name}</label>
                      </CheckboxItem>
                    );
                  })
                )}
              </MultiSelectContainer>
            </SelectField>

            <CheckboxField>
              <Checkbox
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <CheckboxLabel htmlFor="isActive">활성회원</CheckboxLabel>
            </CheckboxField>
          </>
        )}

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            취소
          </CancelButton>
          <SubmitButton type="submit" disabled={loading}>
            {loading ? '저장 중...' : '저장'}
          </SubmitButton>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
